import { Stack, Typography } from '@mui/joy';
import { BellSimple, Checks, WarningCircle } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components/Button';
import { SlidingPanel } from 'core/components/SlidingPanel';
import { Tabs } from 'core/components/Tabs';
import { Loader } from 'core/elements/Loader';
import { ITab } from 'core/types';
import { NOTIFICATION_DRAWER_ID } from 'notifications/constants';
import { BellNotificationListFilter } from 'notifications/enums';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { NotificationDrawerRow } from '../NotificationDrawerRow';

interface IProps {
  notifications: IBellNotification[];
  listFilter: BellNotificationListFilter;
  unreadCount: number;
  onListFilterChange: (filter: BellNotificationListFilter) => void;
  onClick: (notification: IBellNotification) => void;
  onAction: (notification: IBellNotification, cta: IBellNotificationCta) => void;
  onToggleRead: (notification: IBellNotification) => void;
  onDismiss: (id: number) => void;
  onMarkAllRead: () => void;
  onDismissAll: () => void;
  onLoadMore: () => void;
  onClose: () => void;
  isOpen: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  isLoading?: boolean;
  // The list query, not the summary: the summary's unread count can be stale.
  isFetching?: boolean;
  isError?: boolean;
}

const DRAWER_WIDTH = 464;
const STATE_ICON_SIZE = 20;

const STATE_LINE_SX = { alignItems: 'center', gap: 1, paddingX: 3, paddingY: 2 } as const;

export const NotificationDrawer = ({
  notifications,
  listFilter,
  unreadCount,
  onListFilterChange,
  onClick,
  onAction,
  onToggleRead,
  onDismiss,
  onMarkAllRead,
  onDismissAll,
  onLoadMore,
  onClose,
  isOpen,
  hasMore = false,
  isFetchingMore = false,
  isLoading = false,
  isFetching = false,
  isError = false,
}: IProps) => {
  const [isConfirmingClearAll, setIsConfirmingClearAll] = useState(false);

  const { t } = useTranslation();

  // Empty panels: the tab bar sits in the sticky header, the list below it is shared.
  const tabs: ITab[] = useMemo(
    () => [
      { label: t('labelAll'), value: BellNotificationListFilter.ALL, style: { padding: 0 } },
      {
        label: t('labelUnread'),
        value: BellNotificationListFilter.UNREAD,
        style: { padding: 0 },
        badgeProps: { count: unreadCount },
      },
    ],
    [t, unreadCount]
  );

  const hasNotifications = !!notifications.length;
  // Clear all has no unread filter on the server: on Unread it would dismiss the read ones too.
  const canClearAll = BellNotificationListFilter.ALL === listFilter;
  const isEmpty = !hasNotifications && !isLoading && !isFetching;

  const handleOnListFilterChange = (value: string | number) => {
    setIsConfirmingClearAll(false);
    onListFilterChange(value as BellNotificationListFilter);
  };

  const onClearAllClick = () => setIsConfirmingClearAll(true);

  const onCancelClearAll = () => setIsConfirmingClearAll(false);

  const onConfirmClearAll = () => {
    setIsConfirmingClearAll(false);
    onDismissAll();
  };

  const handleOnClose = () => {
    setIsConfirmingClearAll(false);
    onClose();
  };

  const renderListState = () => {
    if (isError && !isFetching) {
      return (
        <Stack direction={Direction.ROW} sx={{ ...STATE_LINE_SX, color: 'danger.plainColor' }}>
          <WarningCircle size={STATE_ICON_SIZE} weight="fill" />

          <Typography level={Level.TITLE_SM} sx={{ color: 'inherit' }}>
            {t('labelNotificationsLoadFailed')}
          </Typography>
        </Stack>
      );
    }

    if (isEmpty) {
      return (
        <Stack direction={Direction.ROW} sx={{ ...STATE_LINE_SX, color: 'text.tertiary' }}>
          <BellSimple size={STATE_ICON_SIZE} />

          <Typography level={Level.TITLE_SM} sx={{ color: 'inherit' }}>
            {t('labelNotificationsEmpty')}
          </Typography>
        </Stack>
      );
    }

    if (!hasNotifications) {
      return (
        <Stack sx={STATE_LINE_SX}>
          <Loader size={Size.SM} alignItems="flex-start" style={{ justifyContent: 'flex-start' }} />
        </Stack>
      );
    }

    if (!hasMore) {
      return null;
    }

    return (
      <Stack sx={{ alignItems: 'flex-start', paddingX: 3, paddingY: 1 }}>
        <Button
          onClick={onLoadMore}
          text={t('buttonLabelLoadMore')}
          variant={Variant.PLAIN}
          loading={isFetchingMore}
        />
      </Stack>
    );
  };

  const footer = isConfirmingClearAll ? (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography level={Level.TITLE_SM} sx={{ color: 'danger.plainColor' }}>
        {t('labelClearAllNotificationsConfirm')}
      </Typography>

      <Button onClick={onConfirmClearAll} text={t('buttonLabelClearAll')} color={Color.DANGER} />

      <Button
        onClick={onCancelClearAll}
        text={t('buttonLabelCancel')}
        variant={Variant.OUTLINED}
        color={Color.NEUTRAL}
      />
    </Stack>
  ) : (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
      <Button
        onClick={onMarkAllRead}
        text={t('buttonLabelMarkAllAsRead')}
        startDecorator={<Checks />}
        disabled={!unreadCount}
      />

      {canClearAll && (
        <Button
          onClick={onClearAllClick}
          text={t('buttonLabelClearAll')}
          variant={Variant.OUTLINED}
          color={Color.NEUTRAL}
          disabled={!hasNotifications}
        />
      )}
    </Stack>
  );

  return (
    <SlidingPanel
      id={NOTIFICATION_DRAWER_ID}
      isOpen={isOpen}
      onClosePanel={handleOnClose}
      title={t('labelNotifications')}
      extra={
        <Tabs
          tabs={tabs}
          ariaLabel={t('labelNotifications')}
          defaultTab={listFilter}
          onChange={handleOnListFilterChange}
          sx={{ height: 'auto', backgroundColor: 'transparent' }}
        />
      }
      buttons={footer}
      keepPageNotificationsOnClose
      sx={{ '--Drawer-horizontalSize': `${DRAWER_WIDTH}px` }}
    >
      {/* spacing 0: the theme's default Stack spacing is a margin, a gap between tinted rows. */}
      <Stack data-testid="notificationDrawerList" spacing={0} sx={{ marginX: -3 }}>
        {notifications.map((notification) => (
          <NotificationDrawerRow
            key={notification.id}
            notification={notification}
            onClick={onClick}
            onAction={onAction}
            onToggleRead={onToggleRead}
            onDismiss={onDismiss}
          />
        ))}

        {renderListState()}
      </Stack>
    </SlidingPanel>
  );
};
